import type { CurrentSession } from "./shared/currentSession";
import {
  canonicalPathFor,
  canAccessPath,
  firstAuditPath,
  getAppDestinations,
  hasMultipleProductAreas,
  recognizeProductPath,
} from "./shared/appDestinations";

export type AppView =
  | "login"
  | "esqueci-senha"
  | "reset-password"
  | "dashboard"
  | "admin-dashboard"
  | "staff-camareira"
  | "lavanderia"
  | "auditoria"
  | "inspecoes"
  | "manutencao"
  | "apps"
  | "access-denied";

export type LoginDestination =
  | { view: Exclude<AppView, "access-denied">; path?: string }
  | { view: "access-denied"; message: string };

function viewForPath(path: string): Exclude<AppView, "login" | "esqueci-senha" | "reset-password" | "access-denied"> | null {
  return recognizeProductPath(path)?.view ?? null;
}

function defaultDestination(session: CurrentSession): LoginDestination {
  const destinations = getAppDestinations(session);
  if (session.user.user_type === "client") {
    const distribution = destinations.find(
      (destination) => destination.id === "distribution",
    );
    if (distribution) {
      return {
        view: "dashboard",
        path: distribution.path,
      };
    }
  }
  if (hasMultipleProductAreas(session)) {
    return { view: "apps" };
  }
  if (destinations.length === 1) {
    const path = destinations[0].path;
    const view = viewForPath(path);
    if (view) return { view, path };
  }
  return {
    view: "access-denied",
    message: "Sua conta foi autenticada, mas não possui acesso a um aplicativo.",
  };
}

export function resolveLoginDestination(
  session: CurrentSession,
  returnPath?: string | null,
): LoginDestination {
  const normalizedPath = (returnPath || "").toLowerCase();
  const returnView = viewForPath(normalizedPath);
  if (returnView) {
    if (returnView === "apps") {
      return getAppDestinations(session).length > 0
        ? { view: "apps", path: "/aplicativos" }
        : defaultDestination(session);
    }
    if (canAccessPath(session, normalizedPath)) {
      const path = canonicalPathFor(session, returnPath || normalizedPath);
      return {
        view: viewForPath(path) || returnView,
        path,
      };
    }
    if (recognizeProductPath(normalizedPath)?.area === "audit") {
      const canAccessAnAuditModule = session.permissions.some((permission) =>
        [
          "maintenance.audit",
          "inspections.audit.governance",
          "inspections.audit.vehicle",
        ].includes(permission),
      );
      return canAccessAnAuditModule
        ? { view: "auditoria", path: firstAuditPath(session) }
        : { view: "apps", path: "/aplicativos" };
    }
    return {
      view: "access-denied",
      message: "Você não possui acesso ao endereço solicitado.",
    };
  }
  return defaultDestination(session);
}

export function isReturnablePath(path: string) {
  const normalizedPath = path.toLowerCase();
  return Boolean(viewForPath(normalizedPath));
}
